import { FC, useState, useCallback, useRef, SyntheticEvent } from 'react'

import { useIsSmallDevice } from '../../hooks/useWindowSize'
import { CheckboxGroupProps } from '../FormElements/CheckboxGroup'
import { SectionTitleProps } from '../FormElements/SectionTitle'
import { SliderProps } from '../FormElements/Slider'
import { FORM_ELEMENTS } from '../FormElements/types'

import * as SC from './styled'

export type FormElement = CheckboxGroupProps | SectionTitleProps | SliderProps

export type RecipesFiltersProps = {
  className?: string
  filterButtonText?: string
  filterTitleText?: string
  activeFiltersCount?: number
  applyButtonText?: string
  applyButtonFunction?: (ev?: SyntheticEvent) => void
  closeButtonFunction?: (ev?: SyntheticEvent) => void
  formElements?: FormElement[]
  onOpenFiltersClick?: (ev: SyntheticEvent) => void
}

const RecipesFilters: FC<RecipesFiltersProps> = (props) => {
  const {
    className,
    filterButtonText,
    filterTitleText,
    activeFiltersCount,
    applyButtonText,
    formElements,
    applyButtonFunction,
    closeButtonFunction,
    onOpenFiltersClick,
  } = props
  const [isOpenPanel, setIsOpenPanel] = useState<boolean>(false)
  const $recipesFilterRef = useRef<HTMLElement>(null)
  const isSmallDevice = useIsSmallDevice()

  const toggleFiltersPanel = useCallback(
    (action: 'close' | 'open' | 'apply', ev: SyntheticEvent) => {
      if (action === 'open') {
        ev && onOpenFiltersClick?.(ev)
      }
      if (action === 'apply') {
        applyButtonFunction?.(ev)
      }

      if (action === 'close') {
        closeButtonFunction?.(ev)
      }

      if (action === 'apply' || action === 'close') {
        const offset = isSmallDevice ? 90 : 190
        const bodyRect = document.body.getBoundingClientRect().top
        const elementRect =
          $recipesFilterRef?.current?.getBoundingClientRect().top ?? 0
        const elementPosition = elementRect - bodyRect
        const offsetPosition = elementPosition - offset

        window?.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      }

      setIsOpenPanel((prev) => !prev)
    },
    [
      onOpenFiltersClick,
      applyButtonFunction,
      closeButtonFunction,
      isSmallDevice,
    ]
  )

  return (
    <SC.Container className={className} ref={$recipesFilterRef}>
      {!isOpenPanel && (
        <SC.FilterButton
          text={filterButtonText}
          variantType="button"
          variantColorScheme="black"
          exponent={activeFiltersCount ? activeFiltersCount?.toString() : ''}
          actionProps={{ onClick: (ev) => toggleFiltersPanel('open', ev) }}
        />
      )}
      {isOpenPanel && (
        <SC.FiltersPanel>
          <SC.HeroPanel>
            {filterTitleText && (
              <SC.FilterTitle>{filterTitleText}</SC.FilterTitle>
            )}
            <SC.CloseCross
              text={'Fermer'}
              icon="close"
              variantType="button"
              variantColorScheme="black"
              variantIcon="only"
              actionProps={{
                onClick: (ev) => toggleFiltersPanel('close', ev),
                'aria-label': 'Fermer',
              }}
            />
          </SC.HeroPanel>
          <SC.InnerPanel>
            {formElements &&
              formElements?.map((formElement) => {
                switch (formElement.type) {
                  case FORM_ELEMENTS.SECTION_TITLE:
                    return <SC.Title {...formElement} />
                  case FORM_ELEMENTS.CHECKBOX_GROUP:
                    return <SC.Checkbox {...formElement} />
                  case FORM_ELEMENTS.SLIDER:
                    return <SC.SliderPicker {...formElement} />
                  default:
                    return null
                }
              })}
            <SC.ApplyButton
              text={applyButtonText}
              variantType="button"
              variantColorScheme="black"
              actionProps={{ onClick: (ev) => toggleFiltersPanel('apply', ev) }}
            />
          </SC.InnerPanel>
        </SC.FiltersPanel>
      )}
    </SC.Container>
  )
}

export default RecipesFilters
